// Apolo IDE — RulesTab side-panel (Sprint v11 E4).
//
// YAML editor for the session's .apolorules. Loads the current rules on
// mount, validates on save (Worker side echoes the parse result), and
// surfaces errors inline. Empty content clears the rules.

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, FileWarning, Loader2, Save } from 'lucide-react';
import { apiUrl } from '../../../lib/apiBase.js';
import { getStoredToken } from '../../../lib/api.js';

const STATUS_IDLE = 'idle';
const STATUS_LOADING = 'loading';
const STATUS_SAVING = 'saving';
const STATUS_SAVED = 'saved';
const STATUS_ERROR = 'error';

const PLACEHOLDER = [
  'agent_persona: "Senior TypeScript engineer at fintech startup"',
  '',
  'file_patterns:',
  '  test: "**/*.test.{ts,tsx,js,jsx}"',
  '  component: "src/components/**/*.{tsx,jsx}"',
  '',
  'coding_style:',
  '  - "Prefer const + arrow functions"',
  '  - "No comments unless WHY is non-obvious"',
  '',
  'forbidden_patterns:',
  '  - "console.log"',
  '  - "any types"',
  '',
  'test_command: "npm test"',
  'lint_command: "npm run lint"',
].join('\n');

async function fetchJSON(path, init = {}) {
  const token = getStoredToken();
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  let body = null;
  try { body = await res.json(); } catch { body = null; }
  return { ok: res.ok, status: res.status, body };
}

export default function RulesTab({ sessionId }) {
  const [yaml, setYaml] = useState('');
  const [parsed, setParsed] = useState(null);
  const [status, setStatus] = useState(STATUS_LOADING);
  const [errorMsg, setErrorMsg] = useState(null);

  // Initial load whenever the session changes.
  useEffect(() => {
    if (!sessionId) {
      setStatus(STATUS_IDLE);
      setYaml('');
      setParsed(null);
      setErrorMsg(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setStatus(STATUS_LOADING);
      const res = await fetchJSON(`/api/code-ide/sessions/${sessionId}/rules`);
      if (cancelled) return;
      if (!res.ok) {
        setErrorMsg(res.body?.error || `Falha ao carregar rules (HTTP ${res.status})`);
        setStatus(STATUS_ERROR);
        return;
      }
      setYaml(res.body?.rules_yaml || '');
      setParsed(res.body?.parsed || null);
      setStatus(STATUS_IDLE);
      setErrorMsg(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const handleSave = useCallback(async () => {
    if (!sessionId) return;
    setStatus(STATUS_SAVING);
    setErrorMsg(null);
    const res = await fetchJSON(`/api/code-ide/sessions/${sessionId}/rules`, {
      method: 'PUT',
      body: JSON.stringify({ rules_yaml: yaml || null }),
    });
    if (!res.ok) {
      setErrorMsg(res.body?.error || `Falha ao salvar (HTTP ${res.status})`);
      setStatus(STATUS_ERROR);
      return;
    }
    setParsed(res.body?.parsed || null);
    setStatus(STATUS_SAVED);
    setTimeout(() => setStatus(STATUS_IDLE), 1500);
  }, [sessionId, yaml]);

  // Cmd+S inside the textarea triggers save.
  const handleKeyDown = useCallback(
    (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    },
    [handleSave],
  );

  if (!sessionId) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-xs text-apolo-textTertiary">
        Abra uma sessão para configurar regras do projeto.
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <div className="text-xs text-apolo-textTertiary">
        Estas regras viram um bloco de alta prioridade no system prompt da sessão.
        Schema documentado em <code className="rounded bg-apolo-surface3 px-1 py-0.5 text-apolo-textSecondary">apoloRules.js</code>.
      </div>

      <label htmlFor="apolo-rules-yaml" className="sr-only">
        .apolorules YAML
      </label>
      <textarea
        id="apolo-rules-yaml"
        value={yaml}
        placeholder={PLACEHOLDER}
        onChange={(e) => setYaml(e.target.value)}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        disabled={status === STATUS_LOADING || status === STATUS_SAVING}
        className="min-h-[300px] flex-1 resize-none rounded-md border border-apolo-borderSubtle bg-apolo-bg p-3 font-mono text-xs leading-relaxed text-apolo-textPrimary outline-none focus:border-apolo-accent disabled:opacity-50"
      />

      {errorMsg && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-apolo-error/30 bg-apolo-errorBg p-2 text-xs text-apolo-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errorMsg}</span>
        </div>
      )}

      {parsed && status === STATUS_IDLE && !errorMsg && (
        <details className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2 text-xs text-apolo-textSecondary">
          <summary className="cursor-pointer select-none">Parsed preview</summary>
          <pre className="mt-2 overflow-auto whitespace-pre-wrap text-[11px] text-apolo-textTertiary">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        </details>
      )}

      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1 text-[11px] text-apolo-textTertiary">
          {status === STATUS_LOADING && <><Loader2 size={12} className="animate-spin" /> Carregando…</>}
          {status === STATUS_SAVING && <><Loader2 size={12} className="animate-spin" /> Salvando…</>}
          {status === STATUS_SAVED && <><Check size={12} className="text-apolo-success" /> Salvo</>}
          {status === STATUS_ERROR && <><FileWarning size={12} className="text-apolo-error" /> Erro</>}
          {status === STATUS_IDLE && 'Cmd+S salva'}
        </span>
        <button
          type="button"
          onClick={handleSave}
          disabled={status === STATUS_LOADING || status === STATUS_SAVING}
          className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-apolo-accentHover disabled:opacity-50"
        >
          <Save size={12} aria-hidden="true" />
          Salvar
        </button>
      </div>
    </div>
  );
}
